import React, { useState } from 'react';
import { PlayIcon, VideoIcon, Volume2Icon, MaximizeIcon } from 'lucide-react';
interface VideoReplayProps {
  /** URL de Vimeo, Wistia, YouTube o archivo propio. */
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  title?: string;
  /** Texto superpuesto sobre la miniatura, editable desde el constructor. */
  overlayTitle?: React.ReactNode;
  className?: string;
  /** Ancho máximo del reproductor. */
  maxWidthClass?: string;
  /**
   * true solo dentro del constructor: muestra el aviso de que falta la URL.
   * En la landing pública nunca aparece ningún mensaje interno.
   */
  editing?: boolean;
  /** Marco configurable desde el editor: esquinas, borde y sombra. */
  frame?: VideoFrame;
}
export interface VideoFrame {
  /** Redondeo en px por dispositivo. 0 deja el vídeo totalmente cuadrado. */
  radius?: number;
  radiusTablet?: number;
  radiusMobile?: number;
  /** Redondeo por esquina: «16 16 0 0». Tiene prioridad sobre `radius`. */
  radiusCustom?: string;
  borderWidth?: number;
  borderColor?: string;
  shadow?: 'ninguna' | 'suave' | 'media' | 'fuerte';
  /** Ancho máximo en px; si falta se usa `maxWidthClass`. */
  maxWidth?: number;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 18px 60px -24px rgba(15,23,42,0.35)',
  media: '0 26px 80px -28px rgba(15,23,42,0.45)',
  fuerte: '0 34px 110px -30px rgba(15,23,42,0.6)'
};

/**
 * Redondeo por dispositivo.
 *
 * Se resuelve con variables CSS y media queries (`.ava-video-frame` en
 * index.css), NO con `window.innerWidth`: así funciona igual en la landing
 * pública y dentro del iframe de la vista previa del constructor, cuyo ancho
 * es el del dispositivo simulado y no el de la ventana.
 */
function frameRadiusVars(frame?: VideoFrame): React.CSSProperties | undefined {
  if (!frame) return undefined;
  const custom = frame.radiusCustom?.trim();
  if (custom) {
    const corners = custom.split(/\s+/).slice(0, 4).map(v => `${parseInt(v, 10) || 0}px`).join(' ');
    return {
      '--v-radius': corners,
      '--v-radius-t': corners,
      '--v-radius-m': corners
    } as React.CSSProperties;
  }
  const px = (value?: number) => value === undefined ? undefined : `${value}px`;
  const base = px(frame.radius);
  if (base === undefined && frame.radiusTablet === undefined && frame.radiusMobile === undefined) {
    return undefined;
  }
  return {
    '--v-radius': base,
    '--v-radius-t': px(frame.radiusTablet ?? frame.radius),
    '--v-radius-m': px(frame.radiusMobile ?? frame.radius)
  } as React.CSSProperties;
}

/** Miniatura automática cuando el usuario no ha subido una propia. */
function autoThumbnail(url: string): string | undefined {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) return `https://i.ytimg.com/vi/${youtube[1]}/hqdefault.jpg`;
  return undefined;
}
function toEmbedUrl(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) {
    return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0&playsinline=1`;
  }
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  // Wistia: se usa el iframe del reproductor, nunca el script de la API ni
  // oEmbed, y solo se crea cuando el visitante pulsa reproducir.
  const wistia = url.match(/wistia\.(?:net|com)\/(?:embed\/(?:iframe|medias)\/|medias\/)([\w-]{6,})/);
  if (wistia) {
    const id = wistia[1].replace(/\.(jsonp|json)$/, '');
    return `https://fast.wistia.net/embed/iframe/${id}?autoPlay=1&playsinline=1&seo=false`;
  }
  return null;
}
function isFileUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Reproductor de vídeo reutilizable en proporción 16:9, protagonista del hero.
 * Se usa en la landing de AVA y en cualquier landing con bloque de vídeo.
 */
export function VideoReplay({
  url,
  thumbnailUrl,
  caption,
  title = 'Vídeo replay',
  overlayTitle,
  className = '',
  maxWidthClass = 'max-w-5xl',
  editing = false,
  frame
}: VideoReplayProps) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = url ? toEmbedUrl(url) : null;
  const hasSource = Boolean(url);
  const radiusVars = frameRadiusVars(frame);
  const poster = thumbnailUrl || (url ? autoThumbnail(url) : undefined);
  // Cuando el editor define un marco propio, se sustituyen las clases por
  // estilos en línea; si no, el vídeo mantiene exactamente su aspecto actual.
  const custom = Boolean(frame && (radiusVars || frame.borderWidth || frame.shadow || frame.maxWidth));
  const shellStyle: React.CSSProperties | undefined = custom ? {
    ...radiusVars,
    borderWidth: frame?.borderWidth ? `${frame.borderWidth}px` : undefined,
    borderStyle: frame?.borderWidth ? 'solid' : undefined,
    borderColor: frame?.borderColor || undefined,
    boxShadow: frame?.shadow ? SHADOWS[frame.shadow] : undefined
  } : undefined;
  return <figure className={`w-full ${frame?.maxWidth ? '' : maxWidthClass} mx-auto ${className}`} style={frame?.maxWidth ? {
    maxWidth: `${frame.maxWidth}px`
  } : undefined} data-id="element-944" data-mp="3qd8dl4j097v4">
      <div className={`ava-video-shell relative w-full overflow-hidden bg-navy ${custom ? 'ava-video-frame' : 'rounded-2xl ring-1 ring-black/5 sm:rounded-3xl'}`} style={shellStyle} data-id="element-945" data-mp="31futvo8wqgu">
        <div className="relative aspect-video w-full" data-id="element-946" data-mp="37fe9n4wqhmjl">
          {/* El iframe del reproductor (YouTube, Vimeo, Wistia o cualquier
              otro) se crea SOLO al pulsar reproducir: al abrir la landing no
              se descarga ningún script ni marco externo, y el hueco 16:9 está
              reservado de antemano, así que no hay salto de maquetación. */}
          {playing && hasSource ? embedUrl ? <iframe src={embedUrl} title={title} loading="lazy" className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-id="element-947" data-mp="vpv2ty8ym8io" /> : isFileUrl(url as string) ? <video src={url} controls autoPlay playsInline className="absolute inset-0 h-full w-full object-cover" data-id="element-948" data-mp="ay6ey4x8bomq" /> : <iframe src={url} title={title} loading="lazy" className="absolute inset-0 h-full w-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen data-id="element-949" data-mp="630r30k9nzw0" /> : <button type="button" onClick={() => hasSource && setPlaying(true)} aria-label={hasSource ? 'Reproducir el vídeo' : 'Vídeo pendiente de configurar'} className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40" data-id="element-950" data-mp="g05hxdp24jj5">
              {poster ? <img src={poster} alt="" width={1280} height={720} decoding="async" fetchPriority="high" className="h-full w-full object-cover" data-id="element-951" data-mp="1zkwe9rrc9ly" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface text-muted-foreground" data-id="element-952" data-mp="3tcfoisa5ejfx">
                  <VideoIcon className="h-8 w-8" aria-hidden="true" data-id="element-953" data-mp="35m38eu4r1ge9" />
                  <span className="text-sm" data-id="element-954" data-mp="30p2yg84s3thv">Miniatura pendiente</span>
                </span>}

              <span className="absolute inset-0 bg-gradient-to-t from-black-premium/35 via-transparent to-transparent" data-id="element-955" data-mp="1g5prigh9ch4h" />

              {/* Texto sobre la miniatura: anclado arriba y con ancho máximo
                  para no solaparse nunca con el botón de reproducción. */}
              {overlayTitle && <span className="absolute inset-x-0 top-3 mx-auto max-w-[88%] whitespace-pre-line px-4 text-center text-[15px] font-bold leading-[1.2] text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] sm:top-[14%] sm:max-w-none sm:px-6 sm:text-3xl" data-id="element-956" data-mp="1b8zclwh6vrkn">
                  {overlayTitle}
                </span>}

              {/* Botón de reproducción */}
              <span className="absolute left-1/2 top-1/2 flex h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out group-hover:scale-105 sm:h-24 sm:w-24" data-id="element-957" data-mp="1tyfdikwsdm1l">
                <span className="absolute inset-0 rounded-full ring-8 ring-white/25" data-id="element-958" data-mp="1a73jkio9jljr" />
                <PlayIcon className="ml-1 h-6 w-6 text-primary sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true" data-id="element-959" data-mp="1a4qsng0j20uk" />
              </span>

              {/* Barra de controles decorativa */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3 text-white/90 sm:px-6 sm:py-4" data-id="element-960" data-mp="118j7i0d7ka4i">
                <PlayIcon className="h-4 w-4 flex-none" fill="currentColor" aria-hidden="true" data-id="element-961" data-mp="1sxctkeqakhm0" />
                <span className="text-[11px] tabular-nums sm:text-xs" data-id="element-962" data-mp="1ow9gfzzmozoh">0:00 / 4:37</span>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-963" data-mp="1k5ax0rjcflh0">
                  <span className="block h-full w-[4%] rounded-full bg-white" data-id="element-964" data-mp="1k2y5pnrp3yra" />
                </span>
                <Volume2Icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-965" data-mp="1f4dfmh65qvgk" />
                <MaximizeIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-966" data-mp="2cmwr2t0xi3eq" />
              </span>

              {/* Aviso solo visible dentro del constructor. */}
              {!hasSource && editing && <span className="absolute inset-x-4 bottom-12 mx-auto w-fit rounded-full bg-white/90 px-3 py-1 text-center text-xs font-medium text-muted-foreground" data-id="element-967" data-mp="2f7l10p20bmge">
                  Añade la URL del vídeo desde el editor
                </span>}
            </button>}
        </div>
      </div>

      {caption && <figcaption className="mx-auto mt-8 max-w-2xl text-center text-base text-muted-foreground sm:text-lg" data-id="element-968" data-mp="2t74618ziuhoh">
          {caption}
        </figcaption>}
    </figure>;
}